import {SafeAreaInsets} from '@ua/web-thomas/Views/ViewModifiers'

import {FixMeAny} from 'airship/types'

export type Context = {
  has_scene?: boolean
  has_survey?: boolean
  capabilities?: {
    has_scene?: boolean
    has_survey?: boolean
  }
}

export type Settings = {
  common: Common
  iaa: SettingsIAA
  iae: SettingsIAE
}

export type Common = {
  colors: Color[]
  fonts: {
    sizes: FontSizes
    stacks?: FontStack[]
  }
  options?: {
    backgroundPush?: boolean
    miss_behavior?: MissBehavior
  }
}

export type SettingsIAA = {
  banner: ViewTypeBanner
  modal: ViewTypeModal
  fullscreen: ViewTypeFullScreen
  html: ViewTypeHTML
}

export type SettingsIAE = {
  typographies: Typography[]
  backgrounds: BackgroundIAE[]
  buttons: ButtonIAE[]
  dismiss: DismissIAE
  indicators: IndicatorsColor
  background: {
    color: Color['id']
  }
  embedded?: {
    items: Embedded[]
  }
  migration?: {
    ui_last_save?: string // iso date
    iaa_to_axp?: string // iso date | IGNORE
  }
} & KeyValue // we have additional key but we don't handle it

export interface BaseElement {
  id: string
  name: string // limit TBD
}

export type DimensionUnit = {
  value: number // if % 1 to 100
  unit: 'px' | '%'
}

export type Dimension = {
  default: DimensionUnit
  min: DimensionUnit
  max: DimensionUnit
}

export enum BackgroundPosition {
  Top = 'top',
  Middle = 'middle',
  Bottom = 'bottom',
}

export type BackgroundPositionItem = {
  id: BackgroundPosition
  label: string
}

export const BACKGROUND_POSITION_ITEMS: BackgroundPositionItem[] = [
  {id: BackgroundPosition.Top, label: 'Top'},
  {id: BackgroundPosition.Middle, label: 'Middle'},
  {id: BackgroundPosition.Bottom, label: 'Bottom'},
]

export interface BackgroundIAE extends BaseElement {
  height: Dimension
  width: Dimension
  position: BackgroundPosition
  borderRadius: number
  clientId?: string
}
export interface Typography extends BaseElement {
  font: string
  size: number
  alignment: TextAlignment
  style: FontStyle
  color: Color['id']
}

export type ButtonIAEBorder = {
  radius: number
  size: number
  color: Color['id']
}

export interface Embedded extends BaseElement {
  clientId: string // id define by client in our app
  width: Dimension
  height: Dimension
  description?: string
}

export interface ButtonIAE extends BaseElement {
  typography: Typography['id']
  color: Color['id']
  border: ButtonIAEBorder
}

export interface DismissIAE {
  color: Color['id']
}

export interface IndicatorsColor {
  active: {color: Color['id']}
  inactive: {color: Color['id']}
}
export enum Tabs {
  COLORS = 'colors',
  DESIGN = 'design',
  OPTIONS = 'options',
  FONTS = 'fonts',
}

export enum AXPTabs {
  BACKGROUND = 'axp_windows_format',
  TEXT = 'axp_text',
  BUTTONS = 'axp_buttons',
}

export enum ViewStylesTabs {
  COLORS = 'viewstyles_colors',
  VIEWSTYLE = 'viewstyles_scenes',
  EMBEDDED = 'viewstyles_embedded',
}

export type ColorPageContext = {
  defaults?: {
    colors?: Color[]
  }
  zoltar_style_defaults?: {
    // reports page has defaults in zoltar_style_defaults
    colors?: Color[]
  }
}

export type BackgroundPageContext = {
  defaults?: {
    axp?: {backgrounds?: BackgroundIAE[]; embedded?: {items: BackgroundIAE[]}}
  }
}

export interface Color {
  id: string // uuid
  name: string
  lightmode: string // color hex ex: #FFFFFF
  lightmode_opacity?: number // 0 of 100
  darkmode?: string // color hex ex: #FFFFFF
  darkmode_opacity?: number // 0 of 100
}

type KeyValue = {[key: string]: FixMeAny}

export enum ViewType {
  BANNER = 'banner',
  MODAL = 'modal',
  FULLSCREEN = 'fullscreen',
  HTML = 'html',
}

export enum DarkModeType {
  LIGHTMODE = 'lightmode',
  DARKMODE = 'darkmode',
}

export interface DisplayDimensions {
  height: number
  width: number
}

export interface DeviceFrame extends DisplayDimensions {
  id: string
  displayName: string
  safeArea: SafeAreaInsets // from web-thomas types
  frameRadius: number
}

export type ViewTypeItem = {id: ViewType; label: string}

export enum Placement {
  TOP = 'top',
  BOTTOM = 'bottom',
}

export type PlacementItem = {id: Placement; label: string}

export enum TextSection {
  HEADING = 'heading',
  BODY = 'body',
  FOOTER = 'footer',
}

export enum Size {
  LARGE = 'large',
  MEDIUM = 'medium',
  SMALL = 'small',
}

export type SizeItem = {id: Size; label: string}

export interface FontStyle {
  italic: boolean
  bold: boolean
  underlined: boolean
  [key: string]: boolean
}

export interface BackgroundFields {
  background_color: string
  dismiss_action_color: string
  placement: Placement
  border_radius: number
  allow_fullscreen_display?: boolean
}

export type BackgroundModalHTML = Omit<BackgroundFields, 'placement'>

export type BackgroundFullScreen = Omit<
  BackgroundFields,
  'palcement' | 'border_radius' | 'allow_fullscreen_display'
>

export type BackgroundBanner = Omit<
  BackgroundFields,
  'allow_fullscreen_display'
>

export const ALIGNMENTS = ['left', 'center', 'right'] as const

export type TextAlignment = (typeof ALIGNMENTS)[number]

export interface Heading {
  heading_text_color: string
  heading_size: string
  heading_alignment: TextAlignment
  heading_style: FontStyle
  heading_font_family: string
}
export interface Body {
  body_text_color: string
  body_size: string
  body_alignment: TextAlignment
  body_style: FontStyle
  body_font_family: string
}

export interface Footer {
  footer_text_color: string
  footer_size: string
  footer_alignment: TextAlignment
  footer_style: FontStyle
  footer_font_family: string
}

export type TextFields = Heading & Body & Footer

export type Button = {
  background_color: string
  border_color: string
  border_radius: number
  label_size: Size
  text?: string
  text_color: string
  font_family: string
}

export interface Buttons {
  buttons: Button[]
}

export type ViewTypeBanner = BackgroundBanner &
  Heading &
  Body &
  Buttons &
  KeyValue // we have additional key but we don't handle it

export type ViewTypeModal = BackgroundModalHTML &
  Heading &
  Body &
  Footer &
  Buttons &
  KeyValue // we have additional key but we don't handle it

export type ViewTypeFullScreen = BackgroundFullScreen &
  Heading &
  Body &
  Footer &
  Buttons &
  KeyValue // we have additional key but we don't handle it

export type ViewTypeHTML = BackgroundModalHTML & KeyValue

export enum FallthroughType {
  SERIF = 'serif',
  SANS_SERIF = 'sans-serif',
}

export interface FontStackUrlData {
  url: string
  mimeType: string
}

export interface FontStack {
  fallthrough: FallthroughType
  fonts: string[]
  name: string
  urlData?: FontStackUrlData
}

export interface Font {
  id: string
  label: string
}

export enum FontSizeKey {
  HEADING = 'heading',
  BODY = 'body',
  FOOTER = 'footer',
  BUTTON = 'button',
}

export enum LabelSize {
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
}
export type MissBehavior = 'skip' | 'cancel' | 'penalize'

export type FontSize = {
  [K in LabelSize]: number
}

export type FontSizes = {
  [K in FontSizeKey]: FontSize
}

export type IaaSettingsField =
  | ViewTypeBanner
  | ViewTypeModal
  | ViewTypeFullScreen
  | ViewTypeHTML

export type IAASettingsFields = {
  banner: ViewTypeBanner
  modal: ViewTypeModal
  fullscreen: ViewTypeFullScreen
  html: ViewTypeHTML
  font_sizes: FontSizes
  font_stacks?: FontStack[]
  audience?: {miss_behavior: MissBehavior}
} & KeyValue // we have additional key but we don't handle it

export type ButtonViewType =
  | ViewType.BANNER
  | ViewType.FULLSCREEN
  | ViewType.MODAL

export type PageContext = {
  capabilities?: {
    has_dark_mode?: boolean
  }
  has_dark_mode?: boolean
}

export type Item = {id: string; label: string}
